import Logger from '../utils/logger.js';

const MODULE = 'UrlVideoSync';

/** Normalizes a URL so sources from different senders can be compared reliably. */
export function toComparableUrl(url: string | null | undefined): string | null {
    if (!url) {
        return null;
    }

    try {
        return new URL(url, window.location.href).toString();
    } catch {
        const trimmed = url.trim();
        return trimmed.length > 0 ? trimmed : null;
    }
}

export interface UrlVideoSyncState {
    url: string;
    comparableUrl: string;
    startAtMs: number;
    seekSeconds: number;
}

/** The room-wide URL share currently in effect, retained so late joiners can catch up. */
export interface SharedUrlVideoShare {
    url: string;
    startAtMs: number;
    requestedBy: number;
    seekSeconds: number;
}

/**
 * Keeps a plain `<video src="...">` element aligned with the room-wide playback clock.
 *
 * URL shares are not routed through RTCPeerConnections: every client fetches the media
 * itself, so the only thing keeping viewers together is this wall-clock alignment.
 */
export class UrlVideoSync {
    private static readonly LEAD_SLACK_MS = 80;
    private static readonly CORRECTION_THRESHOLD_SECONDS = 0.35;
    private static readonly MIN_CORRECTION_INTERVAL_MS = 1400;
    private static readonly LATE_JOIN_THRESHOLD_MS = 1000;

    private playTimeout: number | undefined;
    private lastCorrectionAtMs = 0;
    private state: UrlVideoSyncState | null = null;

    get active(): UrlVideoSyncState | null {
        return this.state;
    }

    get activeComparableUrl(): string | null {
        return this.state?.comparableUrl ?? null;
    }

    /**
     * Points the video element at `url` and schedules playback to begin at `startAtMs`.
     * Returns false when the URL cannot be used.
     */
    begin(
        video: HTMLVideoElement,
        url: string,
        startAtMs: number,
        seekSeconds: number,
        callbacks?: {
            onPlay?: (isLateJoinCatchup: boolean) => void;
            onError?: (message: string) => void;
        }
    ): boolean {
        const comparableUrl = toComparableUrl(url);
        if (!comparableUrl) {
            Logger.warn(MODULE, 'Ignoring shared URL playback request with unusable URL', url);
            return false;
        }

        this.clear();

        const normalizedStartAtMs = Number.isFinite(startAtMs) ? startAtMs : Date.now();
        const normalizedSeek = Number.isFinite(seekSeconds) ? Math.max(0, seekSeconds) : 0;
        const isLateJoinCatchup = Date.now() - normalizedStartAtMs > UrlVideoSync.LATE_JOIN_THRESHOLD_MS;

        this.state = {
            url,
            comparableUrl,
            startAtMs: normalizedStartAtMs,
            seekSeconds: normalizedSeek,
        };

        video.srcObject = null;
        video.src = url;
        video.load();

        const applySyncedStart = () => {
            this.seekToSharedClock(video, normalizedStartAtMs, normalizedSeek);

            const playNow = () => {
                video.play().catch((err) => {
                    // Autoplay with sound can be blocked; fall back to muted so the picture still shows.
                    if ((err as Error)?.name === 'NotAllowedError' && !video.muted) {
                        video.muted = true;
                        video.play().catch((mutedErr) => {
                            Logger.warn(MODULE, 'Shared URL playback failed to start even when muted', mutedErr);
                        });
                        return;
                    }
                    Logger.warn(MODULE, 'Shared URL playback failed to start', err);
                });
                callbacks?.onPlay?.(isLateJoinCatchup);
            };

            const delayMs = normalizedStartAtMs - Date.now();
            if (delayMs > UrlVideoSync.LEAD_SLACK_MS) {
                this.playTimeout = window.setTimeout(() => {
                    this.playTimeout = undefined;
                    playNow();
                }, delayMs);
            } else {
                playNow();
            }
        };

        video.addEventListener('loadedmetadata', applySyncedStart, { once: true });

        // `crossOrigin = 'anonymous'` is mandatory for WebGL texture upload, so a host without
        // permissive CORS headers fails here even though the network request itself succeeds.
        video.addEventListener(
            'error',
            () => {
                if (this.state?.comparableUrl !== comparableUrl) {
                    return;
                }
                const code = video.error?.code;
                const message =
                    code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED
                        ? 'Shared video could not be played. The host must allow cross-origin access (CORS) and serve a browser-playable format such as MP4/WebM.'
                        : 'Shared video failed to load.';
                Logger.warn(MODULE, `${message} (url=${url}, code=${code ?? 'unknown'})`);
                callbacks?.onError?.(message);
            },
            { once: true }
        );

        return true;
    }

    /** Applies a drift correction from a resync ping. Pings for other URLs are ignored. */
    applyPing(video: HTMLVideoElement, url: string, startAtMs: number, seekSeconds: number): boolean {
        if (!this.state) {
            return false;
        }

        const pingUrl = toComparableUrl(url);
        if (!pingUrl || pingUrl !== this.state.comparableUrl) {
            return false;
        }

        this.state.startAtMs = Number.isFinite(startAtMs) ? startAtMs : this.state.startAtMs;
        this.state.seekSeconds = Number.isFinite(seekSeconds) ? Math.max(0, seekSeconds) : this.state.seekSeconds;

        return this.correctDrift(video, this.state.startAtMs, this.state.seekSeconds);
    }

    clear(): void {
        if (this.playTimeout !== undefined) {
            clearTimeout(this.playTimeout);
            this.playTimeout = undefined;
        }
        this.lastCorrectionAtMs = 0;
        this.state = null;
    }

    private correctDrift(video: HTMLVideoElement, startAtMs: number, seekSeconds: number): boolean {
        if (video.readyState < video.HAVE_METADATA) {
            return false;
        }

        const nowMs = Date.now();
        const targetSeconds = this.computeTargetSeconds(video, startAtMs, seekSeconds);
        const currentSeconds = Number.isFinite(video.currentTime) ? video.currentTime : 0;
        const absDriftSeconds = Math.abs(targetSeconds - currentSeconds);

        if (absDriftSeconds < UrlVideoSync.CORRECTION_THRESHOLD_SECONDS) {
            return false;
        }

        // Rate-limit small corrections so playback does not stutter on every ping.
        if (nowMs - this.lastCorrectionAtMs < UrlVideoSync.MIN_CORRECTION_INTERVAL_MS && absDriftSeconds < 1.2) {
            return false;
        }

        try {
            video.currentTime = targetSeconds;
            this.lastCorrectionAtMs = nowMs;
            return true;
        } catch (error) {
            Logger.warn(MODULE, 'Failed to apply drift correction', error);
            return false;
        }
    }

    private seekToSharedClock(video: HTMLVideoElement, startAtMs: number, seekSeconds: number): void {
        try {
            video.currentTime = this.computeTargetSeconds(video, startAtMs, seekSeconds);
        } catch (error) {
            Logger.warn(MODULE, 'Unable to set initial synced currentTime', error);
        }
    }

    private computeTargetSeconds(video: HTMLVideoElement, startAtMs: number, seekSeconds: number): number {
        const elapsedSeconds = Math.max(0, (Date.now() - startAtMs) / 1000);
        const desiredSeconds = Math.max(0, seekSeconds + elapsedSeconds);
        const duration = Number.isFinite(video.duration) ? video.duration : Number.POSITIVE_INFINITY;

        return Math.min(desiredSeconds, duration > 0 ? Math.max(duration - 0.05, 0) : desiredSeconds);
    }
}
